import { Injectable } from '@angular/core';
import { ConfigService } from 'providers/config.service';
import { AuthService } from 'providers/auth.service';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class ConnectionsApiService {
    constructor(
        private http: HttpClient,
        private authService: AuthService,
        private config: ConfigService
    ) {}

    /**
     * Get a list of charging station's connections (active or inactive)
     */
    async getConnections(
        stationId: string,
        queryParams?: GetConnectionsQueryParameters
    ): Promise<GetConnectionsResponse> {
        return this.http
            .get<GetConnectionsResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/connection?${Object.keys(
                    queryParams || {}
                )
                    .reduce(
                        (acc, k) =>
                            queryParams[k] !== undefined ? [...acc, `${k}=${encodeURIComponent(queryParams[k])}`] : acc,
                        []
                    )
                    .join('&')}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Update a charging station's connection
     */
    async putConnection(stationId: string, body: PutConnectionRequest): Promise<PutConnectionResponse> {
        return this.http
            .put<PutConnectionResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/connection`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Disconnect an active connection, or mark connection as dropped for a charging station
     */
    async deleteConnection(
        stationId: string,
        connectionId: string,
        body: DeleteConnectionRequest
    ): Promise<DeleteConnectionResponse> {
        return this.http
            .delete<DeleteConnectionResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/connection/${encodeURIComponent(connectionId)}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                    body,
                } as any
            )
            .toPromise() as any as Promise<DeleteConnectionResponse>;
    }
}

export interface DeleteConnectionRequest {
    reason?: string;
}

export interface DeleteConnectionResponse {
    success: true;
    disconnected: boolean;
    disconnectFailedMessage?: string;
}

export interface GetConnectionsResponse {
    activeConnection: Connection;
    /**
     * Connections ended in the past. NOTE: with consolidate=true, will include activeConnection (if have). Results might conflict wih rearEndConnection. Checks before use.
     */
    pastConnections: Connection[];
    rearEndConnection?: Connection;
    rearStartConnection?: Connection;
}

export interface GetConnectionsQueryParameters {
    /**
     * If startTime and endTime are provided limit is not used
     */
    limit?: number;
    /**
     * Should skip reconnects (distance between connections below offlineThresholdMs). Also include active connection (if have) in pastConnections array. Results might conflict with includeRearPastConnections.
     */
    consolidate?: boolean;
    offlineThresholdMs?: number;
    /**
     * ISO timestamp
     */
    startTime?: string;
    /**
     * ISO timestamp
     */
    endTime?: string;
    /**
     * Include 1 nearest connection period before oldest past period and 1 after the latest past period (>= offlineThresholdMs if possible)
     * Example use case: displaying next connection periods before the starting point and ending point of a timeline
     */
    includeRearPastConnections?: boolean;
}

export interface Connection {
    /**
     * Connection unique id.
     */
    connectionId: string;
    /**
     * Connection start time in ISO8601.
     */
    connectionStartTime: string;
    /**
     * Connection end time in ISO8601.
     */
    connectionEndTime: string;
    /**
     * IP address of the client
     */
    clientIpAddress: string;
    /**
     * Country code of the client, as detected from IP address (by CloudFront in the 'Cloudfront-Viewer-Country' header).
     * The value is a two-letter country code (ISO 3166-1 alpha 2), like US (for USA) or TW (for Taiwan).
     * See https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2 for a list of countries.
     */
    clientCountryCode?: string;
    /**
     * Why disconnected
     */
    disconnectReason?: string;
}

export interface PutConnectionRequest {
    /**
     * Connection unique id.
     */
    connectionId: string;
    /**
     * Connection start time in ISO8601.
     */
    connectionStartTime: string;
    /**
     * IP address of the client
     */
    clientIpAddress: string;
    /**
     * Country code of the client, as detected from IP address (by CloudFront in the 'Cloudfront-Viewer-Country' header).
     * The value is a two-letter country code (ISO 3166-1 alpha 2), like US (for USA) or TW (for Taiwan).
     * See https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2 for a list of countries.
     */
    clientCountryCode?: string;
}

export interface PutConnectionResponse {
    registered: true;
}
